/**
 * ComplianceDossier — A4 portrait inspection document.
 *
 * Layout rules (strict):
 *
 *   • Every page is a fixed 794 × 1123 px frame — real A4 at 96dpi — so
 *     the on-screen preview and the offscreen html2canvas rasteriser
 *     produce pixel-identical output.
 *
 *   • Every page uses the same flex-column layout with three regions:
 *         Header  — 92 px, shrink-0
 *         Content — flex-1, overflow-hidden, px-14 py-8 (safe margins)
 *         Footer  — 44 px, shrink-0
 *     No absolute-positioned footers. No content that can bleed off
 *     the page.
 *
 *   • Horizontal safe zone: 56 px (px-14). No element bleeds edge to
 *     edge except the dark hero bands and the dark-tone page frame.
 *
 *   • Typography scale (fixed):
 *         hero  — 34 / 40 semibold display
 *         h1    — 22 / 26 semibold display
 *         h2    — 15 / 20 semibold display
 *         body  — 11.5 / 16
 *         small — 10 / 14
 *         micro — 9 / 12
 *         label — 9 uppercase tracking-[0.22em]
 *         mono  — tabular-nums font-mono for IDs, hashes, timestamps
 *
 *   • Colour tokens:
 *         ink    #0B1220
 *         accent #8C6B33 / #D4A65A / #F0D497
 *         muted  slate-500 / slate-400
 *         line   slate-200 / slate-100
 *
 * All data flows in via props. No fetches, no side effects.
 */

import { useMemo } from "react";
import QRCode from "qrcode";
import {
  ShieldCheck,
  BadgeCheck,
  AlertTriangle,
  FileText,
  Users,
  ClipboardCheck,
  CalendarClock,
  CheckCircle2,
  Circle,
  Timer,
  FileClock,
  Award,
} from "lucide-react";

export const DOSSIER_PAGE_PX = { width: 794, height: 1123 };

/* ------------------------------------------------------------------ *
 *  Pagination knobs. Pages are a fixed 1123 px and overflow-hidden, so
 *  these caps carry headroom for rows whose titles wrap to two lines —
 *  real-world data clipped at the previous, tighter values. Employee
 *  rows now continue onto further pages (see employeePageCount), so a
 *  lower cap costs an extra page rather than losing a record.
 * ------------------------------------------------------------------ */
const PAGINATION = {
  staffMatrixPerPage: 8,
  employeeCompetencyRows: 6,
  employeeDocumentRows: 4,
  employeeOutstandingRows: 3,
  documentRegisterPerPage: 12,
  certificateRegisterPerPage: 14,
  timelinePerPage: 16,
};

/* An employee's evidence can exceed a single page. Emit as many pages as
 * the longest of its three lists requires so no row is ever dropped. */
function employeePageCount(emp) {
  return Math.max(
    1,
    Math.ceil((emp.competencyRows?.length || 0) / PAGINATION.employeeCompetencyRows),
    Math.ceil((emp.documentRows?.length || 0) / PAGINATION.employeeDocumentRows),
    Math.ceil((emp.outstanding?.length || 0) / PAGINATION.employeeOutstandingRows)
  );
}

export default function ComplianceDossier({
  business,
  summary,
  team,
  docs,
  certs,
  assignments,
  competencyAssignments = [],
  compliance_settings = {},
  reportId,
  generatedAt = new Date(),
  verifyOrigin,
  focusEmployeeId = null,
}) {
  const derivedFull = useMemo(
    () =>
      deriveMetrics({
        summary,
        team,
        docs,
        certs,
        assignments,
        competencyAssignments,
        compliance_settings,
      }),
    [summary, team, docs, certs, assignments, competencyAssignments, compliance_settings]
  );

  // Individual compliance record — narrow every collection to a single
  // employee so the same components render an HR-ready single-person
  // artefact.
  const focus = focusEmployeeId
    ? derivedFull.employees.find((e) => e.id === focusEmployeeId)
    : null;
  const derived = focus ? narrowToEmployee(derivedFull, focus) : derivedFull;

  const matrixChunks = focus ? [] : chunk(derived.employees, PAGINATION.staffMatrixPerPage);
  const docChunks = focus ? [] : chunk(derived.documents, PAGINATION.documentRegisterPerPage);
  const certChunks = chunk(derived.certificates, PAGINATION.certificateRegisterPerPage);
  const timelineChunks = chunk(derived.timeline, PAGINATION.timelinePerPage);

  // Predictable page numbering across the entire dossier.
  let pageCounter = 0;
  const nextPage = () => ++pageCounter;
  const totalPages =
    1 + // cover
    matrixChunks.length +
    derived.employees.reduce((sum, e) => sum + employeePageCount(e), 0) +
    docChunks.length +
    certChunks.length +
    timelineChunks.length +
    1 + // exceptions
    1; // integrity

  const pageMeta = { reportId, generatedAt, totalPages };

  return (
    <div data-testid="compliance-dossier" className="mx-auto flex flex-col gap-6 print:gap-0">
      <CoverPage
        business={business}
        derived={derived}
        summary={summary}
        focus={focus}
        pageIndex={nextPage()}
        pageMeta={pageMeta}
      />

      {matrixChunks.map((rows, i) => (
        <StaffMatrixPage
          key={`matrix-${i}`}
          business={business}
          rows={rows}
          chunkIndex={i}
          totalChunks={matrixChunks.length}
          pageIndex={nextPage()}
          pageMeta={pageMeta}
        />
      ))}

      {derived.employees.flatMap((emp) => {
        const parts = employeePageCount(emp);
        return Array.from({ length: parts }, (_, part) => (
          <EmployeePage
            key={`emp-${emp.id}-${part}`}
            business={business}
            emp={emp}
            part={part}
            totalParts={parts}
            verifyOrigin={verifyOrigin}
            pageIndex={nextPage()}
            pageMeta={pageMeta}
          />
        ));
      })}

      {docChunks.map((rows, i) => (
        <DocumentRegisterPage
          key={`doc-${i}`}
          business={business}
          rows={rows}
          chunkIndex={i}
          totalChunks={docChunks.length}
          pageIndex={nextPage()}
          pageMeta={pageMeta}
        />
      ))}

      {certChunks.map((rows, i) => (
        <CertificateRegisterPage
          key={`cert-${i}`}
          business={business}
          rows={rows}
          chunkIndex={i}
          totalChunks={certChunks.length}
          verifyOrigin={verifyOrigin}
          pageIndex={nextPage()}
          pageMeta={pageMeta}
        />
      ))}

      {timelineChunks.map((rows, i) => (
        <TimelinePage
          key={`tl-${i}`}
          business={business}
          rows={rows}
          chunkIndex={i}
          totalChunks={timelineChunks.length}
          pageIndex={nextPage()}
          pageMeta={pageMeta}
        />
      ))}

      <ExceptionsPage
        business={business}
        exceptions={derived.exceptions}
        pageIndex={nextPage()}
        pageMeta={pageMeta}
      />

      <IntegrityPage
        business={business}
        derived={derived}
        verifyOrigin={verifyOrigin}
        pageIndex={nextPage()}
        pageMeta={pageMeta}
      />
    </div>
  );
}

/* ================================================================== *
 *  PageFrame — the grid contract every page adheres to.
 * ================================================================== */
function PageFrame({ testid, tone = "light", header, children, footer }) {
  const dark = tone === "dark";
  return (
    <section
      data-dossier-page="1"
      data-testid={testid}
      style={{
        width: `${DOSSIER_PAGE_PX.width}px`,
        height: `${DOSSIER_PAGE_PX.height}px`,
      }}
      className={`relative flex flex-col overflow-hidden shadow-[0_20px_60px_-30px_rgba(11,18,32,0.35)] print:shadow-none ${
        dark
          ? "bg-gradient-to-br from-[#0B1220] via-[#0F1730] to-[#131c31] text-white"
          : "bg-white text-slate-900"
      } font-sans`}
    >
      {header}
      <div className="relative flex-1 min-h-0 overflow-hidden px-14 py-8">
        {children}
      </div>
      {footer}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  Header — 92 px, aligned to the same grid as content (px-14)
 * ------------------------------------------------------------------ */
function PageHeader({ kicker, title, subtitle, reportId, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <header
      className={`shrink-0 min-h-[92px] px-14 pt-8 pb-4 flex items-start justify-between gap-6 border-b ${
        dark ? "border-white/10" : "border-slate-200"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div
          className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.24em] font-semibold leading-[1.4] ${
            dark ? "text-[#F0D497]" : "text-[#8C6B33]"
          }`}
        >
          <Award size={9} />
          Compliance Dossier · {kicker}
        </div>
        <h1
          className={`mt-1.5 font-display text-[20px] leading-[1.2] tracking-tight font-semibold break-words [overflow-wrap:anywhere] ${
            dark ? "text-white" : "text-slate-900"
          }`}
        >
          {title}
        </h1>
        {subtitle && (
          <div
            className={`mt-0.5 text-[10.5px] leading-snug break-words ${
              dark ? "text-slate-300" : "text-slate-500"
            }`}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div className="text-right shrink-0 leading-[1.5]">
        <div
          className={`text-[9px] uppercase tracking-[0.22em] font-semibold ${
            dark ? "text-slate-300" : "text-slate-500"
          }`}
        >
          Report ID
        </div>
        <div
          className={`mt-1 font-mono text-[11px] ${
            dark ? "text-white" : "text-slate-900"
          }`}
        >
          {reportId}
        </div>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ *
 *  Footer — 44 px, always at the bottom of the page.
 * ------------------------------------------------------------------ */
function PageFooter({ label, pageIndex, totalPages, generatedAt, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <footer
      className={`shrink-0 h-[44px] px-14 flex items-center justify-between gap-4 border-t text-[9px] uppercase tracking-[0.18em] font-semibold ${
        dark ? "border-white/10 text-slate-300" : "border-slate-200 text-slate-400"
      }`}
    >
      <div className="flex items-center gap-3 truncate">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={9} />
          Signed · SHA-256 · Retained 7y
        </span>
        <span
          className={`normal-case tracking-normal text-[9.5px] truncate ${
            dark ? "text-slate-400" : "text-slate-500"
          }`}
        >
          Generated automatically by CanniComply
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={dark ? "text-slate-300" : "text-slate-500"}>{label}</span>
        <span className="opacity-40">·</span>
        <span className="font-mono normal-case tracking-normal">
          Page {pageIndex} / {totalPages}
        </span>
      </div>
    </footer>
  );
}

/* ================================================================== *
 *  1 · Cover / Executive Summary
 * ================================================================== */
function CoverPage({ business, derived, summary, focus, pageIndex, pageMeta }) {
  const s = summary || {};
  const score = focus ? focus.trainingPct : s.compliance_score ?? 0;
  const staffTrained = s.staff_fully_trained ?? 0;
  const staffTotal = s.staff_total ?? 0;
  const staffOutstanding = Math.max(0, staffTotal - staffTrained);
  const readiness = focus
    ? readinessForEmployee(focus, derived.exceptions.total)
    : readinessLabel(score, derived.exceptions.total);
  const heroKicker = focus ? "Individual Compliance Record" : "Compliance Dossier";
  const preparedFor = focus ? focus.name : business?.name || "Your business";
  const subline = focus
    ? `${focus.role || "Team member"}${focus.department ? " · " + focus.department : ""} · ${business?.name || ""}`
    : `${business?.industry || "Regulated operator"}${business?.country ? " · " + business.country : ""}`;

  return (
    <PageFrame
      testid="dossier-cover"
      footer={
        <PageFooter
          label="Executive summary"
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
      header={null}
    >
      {/* Dark hero band spanning the full page width — sits directly in
          the content flex slot so no absolute positioning is needed. */}
      <div className="absolute inset-x-0 top-0 h-[358px] bg-gradient-to-br from-[#0B1220] via-[#0F1730] to-[#131c31] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.55) 1px, transparent 0)",
            backgroundSize: "22px 22px",
          }}
        />
        <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#D4A65A]/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-emerald-500/15 blur-3xl" />

        <div className="relative h-full px-14 pt-10 pb-6 flex flex-col justify-between">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.24em] font-semibold text-[#F0D497]">
                <Award size={10} />
                {heroKicker}
              </div>
              <div className="mt-5 text-[9.5px] uppercase tracking-[0.24em] text-slate-300 font-semibold">
                {focus ? "Employee" : "Prepared for"}
              </div>
              <h1 className="mt-1.5 font-display text-[32px] leading-[1.15] tracking-tight text-white font-semibold truncate">
                {preparedFor}
              </h1>
              <div className="mt-1 text-[11.5px] text-slate-300 truncate">
                {subline}
              </div>
            </div>
            <div className="text-right shrink-0 leading-[1.5]">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[9.5px] uppercase tracking-[0.18em] font-semibold text-emerald-300">
                <ShieldCheck size={10} />
                Digitally verified
              </div>
              <div className="mt-4 text-[9px] uppercase tracking-[0.22em] font-semibold text-slate-300">
                Report ID
              </div>
              <div className="mt-1 font-mono text-[11.5px] text-white">
                {pageMeta.reportId}
              </div>
              <div className="mt-3 text-[9px] uppercase tracking-[0.22em] font-semibold text-slate-300">
                Generated
              </div>
              <div className="mt-1 font-mono text-[10.5px] text-white">
                {formatDateTime(pageMeta.generatedAt)}
              </div>
            </div>
          </div>

          {/* Score row — two locked columns so text never crashes into the ring */}
          <div className="grid grid-cols-[168px_1fr] gap-8 items-center">
            <ScoreRing score={score} />
            <div className="min-w-0">
              <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#F0D497] font-semibold">
                Inspection readiness
              </div>
              <div
                className={`mt-1 font-display text-[24px] leading-[1.1] tracking-tight font-semibold ${readiness.tone}`}
              >
                {readiness.label}
              </div>
              <p className="mt-1.5 text-[11px] text-slate-300 leading-[1.55]">
                {readiness.body}
              </p>
              <div className="mt-3 h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#D4A65A] via-[#F0D497] to-emerald-400"
                  style={{ width: `${score}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Below the hero — KPI grid + contents outline */}
      <div className="relative pt-[382px] pb-2">
        <div className="grid grid-cols-4 gap-3">
          {focus ? (
            <>
              <KpiTile label="Competencies assigned" value={focus.competenciesAssigned} sub="total scope" />
              <KpiTile label="Competencies completed" value={focus.competenciesCompleted} sub="passed assessments" tone="emerald" />
              <KpiTile label="Training progress" value={`${focus.trainingPct}%`} sub="of assigned work" tone={focus.trainingPct >= 80 ? "emerald" : focus.trainingPct >= 40 ? "amber" : "rose"} />
              <KpiTile label="Certificates" value={focus.certificates} sub="signed evidence" tone="emerald" />
              <KpiTile label="Latest score" value={focus.lastScore != null ? `${focus.lastScore}%` : "—"} sub="most recent" />
              <KpiTile label="Open actions" value={focus.openActions} sub={focus.openActions === 0 ? "nothing outstanding" : "awaiting employee"} tone={focus.openActions > 0 ? "amber" : "slate"} />
              <KpiTile label="Compliance status" value={focus.statusLabel} sub={focus.risk === "low" ? "green" : focus.risk === "medium" ? "amber" : "red"} tone={focus.risk === "low" ? "emerald" : focus.risk === "medium" ? "amber" : "rose"} />
              <KpiTile label="Evidence retention" value="7 yrs" sub="min · immutable" tone="gold" />
            </>
          ) : (
            <>
              <KpiTile label="Staff trained" value={`${staffTrained}`} sub={`of ${staffTotal}`} tone="emerald" />
              <KpiTile
                label="Staff outstanding"
                value={staffOutstanding}
                sub={staffOutstanding === 0 ? "everyone up to date" : "await completion"}
                tone={staffOutstanding > 0 ? "amber" : "slate"}
              />
              <KpiTile label="Active SOPs" value={s.documents_total ?? 0} sub="under version control" />
              <KpiTile label="Certificates issued" value={s.certificates_issued ?? 0} sub="signed evidence" tone="emerald" />
              <KpiTile
                label="Certificates expiring"
                value={derived.expiringCount}
                sub="within 30 days"
                tone={derived.expiringCount > 0 ? "amber" : "slate"}
              />
              <KpiTile
                label="Assessments passed"
                value={derived.assessmentsPassed}
                sub={`avg score ${derived.avgScore}%`}
                tone="emerald"
              />
              <KpiTile
                label="Open exceptions"
                value={derived.exceptions.total}
                sub={derived.exceptions.total === 0 ? "clean workspace" : "see exceptions"}
                tone={derived.exceptions.total > 0 ? "rose" : "slate"}
              />
              <KpiTile label="Evidence retention" value="7 yrs" sub="min · immutable" tone="gold" />
            </>
          )}
        </div>

        <div className="mt-5 rounded-xl border border-[#D4A65A]/30 bg-[#FDF6E7] px-5 py-4">
          <div className="text-[9px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
            This dossier contains
          </div>
          <ul className="mt-2.5 grid grid-cols-2 gap-x-6 gap-y-1 text-[10.5px] text-slate-700 leading-[1.55]">
            {(focus
              ? [
                  "Executive summary — this employee",
                  "Individual employee evidence page",
                  "Signed certificate register for this employee",
                  "Chronological audit timeline for this employee",
                  "Exceptions & risks — this employee",
                  "Evidence integrity confirmation",
                ]
              : [
                  "Executive summary and readiness score",
                  "Staff compliance matrix",
                  "Individual employee evidence pages",
                  "Controlled document register",
                  "Signed certificate register",
                  "Chronological audit timeline",
                  "Exceptions & risks",
                  "Evidence integrity confirmation",
                ]
            ).map((t) => (
              <li key={t} className="flex items-center gap-1.5">
                <Circle size={4} className="fill-[#8C6B33] text-[#8C6B33] shrink-0" />
                <span className="truncate">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </PageFrame>
  );
}

/* ================================================================== *
 *  2 · Staff Compliance Matrix
 * ================================================================== */
function StaffMatrixPage({ business, rows, chunkIndex, totalChunks, pageIndex, pageMeta }) {
  return (
    <PageFrame
      testid={`dossier-matrix-${chunkIndex}`}
      header={
        <PageHeader
          kicker="Workforce"
          title="Staff Compliance Matrix"
          subtitle={`${business?.name || ""}${business?.industry ? " · " + business.industry : ""}`}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          label={`Staff matrix · page ${chunkIndex + 1} of ${totalChunks}`}
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <p className="text-[10.5px] text-slate-500 leading-[1.55] mb-4 max-w-3xl">
        Every employee, their assigned and completed competencies, latest
        assessment score and any outstanding actions. Risk is graded R / A / G
        in the right-hand column.
      </p>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-[10.5px] border-separate border-spacing-0 table-fixed">
          <colgroup>
            <col style={{ width: "22%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "6%" }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 text-left text-[9px] uppercase tracking-[0.14em] text-slate-600 font-semibold">
              <Th>Employee</Th>
              <Th>Role / dept</Th>
              <Th align="center">Assign.</Th>
              <Th align="center">Done</Th>
              <Th>Progress</Th>
              <Th align="center">Certs</Th>
              <Th align="center">Last %</Th>
              <Th align="center">Open</Th>
              <Th align="center">Risk</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-white" : "bg-slate-50/40"}>
                <Td>
                  <div className="text-slate-900 font-semibold truncate">{r.name}</div>
                  <div className="text-slate-500 text-[9px] font-mono truncate">{r.email || "—"}</div>
                </Td>
                <Td>
                  <div className="text-slate-700 truncate">{r.role || "—"}</div>
                  {r.department && (
                    <div className="text-slate-500 text-[9px] truncate">{r.department}</div>
                  )}
                </Td>
                <Td align="center" className="tabular-nums text-slate-700">
                  {r.competenciesAssigned}
                </Td>
                <Td align="center" className="tabular-nums text-slate-900 font-semibold">
                  {r.competenciesCompleted}
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full ${
                          r.trainingPct >= 80
                            ? "bg-emerald-500"
                            : r.trainingPct >= 40
                            ? "bg-[#D4A65A]"
                            : "bg-rose-500"
                        }`}
                        style={{ width: `${r.trainingPct}%` }}
                      />
                    </div>
                    <span className="text-[9px] tabular-nums text-slate-600 font-semibold shrink-0 w-8 text-right">
                      {r.trainingPct}%
                    </span>
                  </div>
                </Td>
                <Td align="center" className="tabular-nums text-emerald-700 font-semibold">
                  {r.certificates}
                </Td>
                <Td align="center" className="tabular-nums">
                  {r.lastScore != null ? (
                    <span className="text-slate-900 font-semibold">{r.lastScore}%</span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </Td>
                <Td align="center" className="tabular-nums">
                  {r.openActions > 0 ? (
                    <span className="text-rose-700 font-semibold">{r.openActions}</span>
                  ) : (
                    <span className="text-slate-300">0</span>
                  )}
                </Td>
                <Td align="center">
                  <RiskDot risk={r.risk} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <div className="mt-6 text-center text-[10.5px] text-slate-500">
          No staff records on this page.
        </div>
      )}
    </PageFrame>
  );
}

/* ================================================================== *
 *  3 · Employee evidence page
 * ================================================================== */
function EmployeePage({
  business,
  emp,
  verifyOrigin,
  pageIndex,
  pageMeta,
  part = 0,
  totalParts = 1,
}) {
  // Slice this page's window out of each list so overflow continues onto
  // the next employee page instead of being discarded.
  const competencyRows = (emp.competencyRows || []).slice(
    part * PAGINATION.employeeCompetencyRows,
    (part + 1) * PAGINATION.employeeCompetencyRows
  );
  const documentRows = (emp.documentRows || []).slice(
    part * PAGINATION.employeeDocumentRows,
    (part + 1) * PAGINATION.employeeDocumentRows
  );
  const outstandingRows = (emp.outstanding || []).slice(
    part * PAGINATION.employeeOutstandingRows,
    (part + 1) * PAGINATION.employeeOutstandingRows
  );
  return (
    <PageFrame
      testid={`dossier-employee-${emp.id}`}
      header={
        <PageHeader
          kicker="Individual record"
          title={`Employee evidence — ${emp.name}${
            totalParts > 1 ? ` · page ${part + 1} of ${totalParts}` : ""
          }`}
          subtitle={`${emp.role || "Team member"}${emp.department ? " · " + emp.department : ""}`}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          label={`Employee evidence · ${emp.name}`}
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <div className="grid grid-cols-[1fr_180px] gap-5 min-w-0">
        {/* LEFT column */}
        <div className="min-w-0">
          <div className="rounded-lg border border-slate-200 px-4 py-3 grid grid-cols-2 gap-y-2 gap-x-6">
            <Kv label="Full name" value={emp.name} />
            <Kv
              label="Employee ID"
              value={<span className="font-mono">{emp.id.slice(0, 8).toUpperCase()}</span>}
            />
            <Kv label="Role" value={emp.role || "—"} />
            <Kv label="Department" value={emp.department || "—"} />
            <Kv
              label="Compliance status"
              value={
                <StatusBadge
                  tone={emp.risk === "low" ? "emerald" : emp.risk === "medium" ? "amber" : "rose"}
                >
                  {emp.statusLabel}
                </StatusBadge>
              }
            />
            <Kv
              label="Training progress"
              value={<span className="tabular-nums font-semibold">{emp.trainingPct}%</span>}
            />
          </div>

          <SectionLabel icon={ClipboardCheck}>Competency training history</SectionLabel>
          <div className="mt-1.5 rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-[9.5px] border-separate border-spacing-0 table-fixed">
              <colgroup>
                <col style={{ width: "36%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "8%" }} />
                {/* Cert ID already truncates gracefully (truncate + title tooltip
                    on its cell), so it can give up 3% to the Completed header,
                    which was clipping at 14% since its Th is whitespace-nowrap
                    with no truncation fallback. */}
                <col style={{ width: "14%" }} />
                <col style={{ width: "17%" }} />
              </colgroup>
              <thead>
                <tr className="bg-slate-50 text-left text-[8.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">
                  <Th tight>Competency</Th>
                  <Th tight align="center">Ver.</Th>
                  <Th tight align="center">Status</Th>
                  <Th tight align="center">%</Th>
                  <Th tight>Cert ID</Th>
                  <Th tight align="center">Completed</Th>
                </tr>
              </thead>
              <tbody>
                {competencyRows.map((r, i) => (
                  <tr key={r.id} className={i % 2 ? "bg-white" : "bg-slate-50/40"}>
                    <Td tight>
                      <span className="font-semibold text-slate-900 truncate block">
                        {r.competency_name}
                      </span>
                    </Td>
                    <Td tight align="center" className="font-mono text-slate-600">
                      {r.version || "1.0"}
                    </Td>
                    <Td tight align="center">
                      <StatusBadge
                        tone={
                          r.status === "completed"
                            ? "emerald"
                            : r.status === "in_progress"
                            ? "amber"
                            : "slate"
                        }
                      >
                        {r.status === "completed"
                          ? "Passed"
                          : r.status === "in_progress"
                          ? "In progress"
                          : "Assigned"}
                      </StatusBadge>
                    </Td>
                    <Td tight align="center" className="tabular-nums">
                      {r.score != null ? `${r.score}` : "—"}
                    </Td>
                    <Td tight className="font-mono text-slate-600 overflow-hidden">
                      <span className="block truncate" title={r.cert_id || ""}>
                        {r.cert_id || "—"}
                      </span>
                    </Td>
                    <Td tight align="center" className="text-slate-600">
                      {formatDate(r.completed_at)}
                    </Td>
                  </tr>
                ))}
                {competencyRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-3 text-center text-slate-400 text-[10px] bg-white"
                    >
                      No competencies assigned yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <SectionLabel icon={FileText}>Document acknowledgements</SectionLabel>
          <ul className="mt-1.5 space-y-1.5">
            {documentRows.map((d) => (
              <li
                key={d.id}
                className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
              >
                <span className="h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <FileText size={10} className="text-slate-500" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] text-slate-900 font-semibold truncate">
                    {d.document_title}
                    <span className="text-slate-400 font-normal font-mono ml-1">
                      {d.document_version || ""}
                    </span>
                  </div>
                  <div className="text-[9.5px] text-slate-500 truncate">
                    Assigned {formatDate(d.assigned_at)}
                    {d.completed_at && ` · Acknowledged ${formatDate(d.completed_at)}`}
                  </div>
                </div>
                <StatusBadge tone={d.status === "completed" ? "emerald" : "amber"}>
                  {d.status === "completed" ? "Signed" : "Open"}
                </StatusBadge>
              </li>
            ))}
            {documentRows.length === 0 && (
              <li className="text-[10.5px] text-slate-400 py-1">No documents assigned.</li>
            )}
          </ul>

          {outstandingRows.length > 0 && (
            <>
              <SectionLabel icon={AlertTriangle}>Outstanding actions</SectionLabel>
              <ul className="mt-1.5 space-y-1">
                {outstandingRows.map((o) => (
                  <li key={o.id} className="flex items-start gap-2 text-[10px] text-rose-700">
                    <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                    <span className="truncate">{o.label}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* RIGHT rail — QR, approvals, integrity */}
        <aside className="space-y-3 min-w-0">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[8.5px] uppercase tracking-[0.22em] font-semibold text-[#8C6B33]">
              Verification
            </div>
            <div className="mt-2 aspect-square rounded-md border border-slate-200 bg-white p-1.5">
              <QrTile value={buildEmployeeVerifyUrl(verifyOrigin, emp)} />
            </div>
            <div className="mt-2 text-[8px] text-slate-500 font-mono leading-[1.35] overflow-hidden">
              <div className="truncate" title={buildEmployeeVerifyUrl(verifyOrigin, emp)}>
                {shortVerifyUrl(buildEmployeeVerifyUrl(verifyOrigin, emp))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[8.5px] uppercase tracking-[0.22em] font-semibold text-[#8C6B33]">
              Manager approval
            </div>
            <div className="mt-1.5 text-[10px] text-slate-700 leading-[1.5]">
              Signed by <b>{emp.approvedBy || business?.name || "Owner"}</b> on behalf of{" "}
              {business?.name || "the organisation"}.
            </div>
            <div className="mt-3 h-8 border-b border-slate-300" />
            <div className="mt-1 text-[8.5px] uppercase tracking-[0.18em] text-slate-400">
              Manager signature
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="text-[8.5px] uppercase tracking-[0.22em] font-semibold text-[#8C6B33]">
              Record integrity
            </div>
            <ul className="mt-1.5 space-y-1 text-[9.5px] text-slate-600 leading-[1.45]">
              <li className="flex gap-1.5">
                <CheckCircle2 size={9} className="text-emerald-600 mt-0.5 shrink-0" />
                Certificates SHA-256 signed
              </li>
              <li className="flex gap-1.5">
                <CheckCircle2 size={9} className="text-emerald-600 mt-0.5 shrink-0" />
                Retained 7 years minimum
              </li>
              <li className="flex gap-1.5">
                <CheckCircle2 size={9} className="text-emerald-600 mt-0.5 shrink-0" />
                Audit events append-only
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </PageFrame>
  );
}

/* ================================================================== *
 *  4 · Controlled document register
 * ================================================================== */
function DocumentRegisterPage({ business, rows, chunkIndex, totalChunks, pageIndex, pageMeta }) {
  return (
    <PageFrame
      testid={`dossier-docs-${chunkIndex}`}
      header={
        <PageHeader
          kicker="Policies & SOPs"
          title="Controlled Document Register"
          subtitle={business?.name}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          label={`Document register · page ${chunkIndex + 1} of ${totalChunks}`}
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <p className="text-[10.5px] text-slate-500 leading-[1.55] mb-4 max-w-3xl">
        Every controlled document with its current version, assigned audience,
        acknowledgement rate and next review status.
      </p>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-[10.5px] border-separate border-spacing-0 table-fixed">
          <colgroup>
            <col style={{ width: "32%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 text-left text-[9px] uppercase tracking-[0.14em] text-slate-600 font-semibold">
              <Th>Document</Th>
              <Th align="center">Ver.</Th>
              <Th align="center">Assign.</Th>
              <Th align="center">Ack.</Th>
              <Th align="center">Passed</Th>
              <Th align="center">Certs</Th>
              <Th align="center">Next review</Th>
              <Th align="center">Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={d.id} className={i % 2 ? "bg-white" : "bg-slate-50/40"}>
                <Td>
                  <div className="text-slate-900 font-semibold truncate">{d.title}</div>
                  {d.description && (
                    <div className="text-slate-500 text-[9px] truncate">{d.description}</div>
                  )}
                </Td>
                <Td align="center" className="font-mono text-slate-700">
                  {d.version || "1.0"}
                </Td>
                <Td align="center" className="tabular-nums text-slate-700">
                  {d.assignedCount ?? 0}
                </Td>
                <Td align="center" className="tabular-nums text-slate-900 font-semibold">
                  {d.completedCount ?? 0}
                </Td>
                <Td align="center" className="tabular-nums text-emerald-700 font-semibold">
                  {d.passedCount ?? 0}
                </Td>
                <Td align="center" className="tabular-nums text-emerald-700">
                  {d.certCount ?? 0}
                </Td>
                <Td align="center" className="text-slate-600 text-[9.5px] truncate">
                  {d.nextReview}
                </Td>
                <Td align="center">
                  <StatusBadge tone={d.reviewTone}>{d.reviewStatus}</StatusBadge>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageFrame>
  );
}

/* ================================================================== *
 *  5 · Certificate register
 * ================================================================== */
function CertificateRegisterPage({
  business,
  rows,
  chunkIndex,
  totalChunks,
  verifyOrigin,
  pageIndex,
  pageMeta,
}) {
  return (
    <PageFrame
      testid={`dossier-certs-${chunkIndex}`}
      header={
        <PageHeader
          kicker="Signed evidence"
          title="Certificate Register"
          subtitle={business?.name}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          label={`Certificate register · page ${chunkIndex + 1} of ${totalChunks}`}
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <p className="text-[10.5px] text-slate-500 leading-[1.55] mb-4 max-w-3xl">
        Every certificate issued for this workspace. Each ID is publicly
        verifiable via the URL beneath the QR block.
      </p>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-[9.5px] border-separate border-spacing-0 table-fixed">
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 text-left text-[8.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">
              <Th tight>Certificate ID</Th>
              <Th tight>Employee</Th>
              <Th tight>Competency</Th>
              <Th tight align="center">Ver.</Th>
              <Th tight align="center">Score</Th>
              <Th tight align="center">Issued</Th>
              <Th tight align="center">Valid until</Th>
              <Th tight align="center">Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={c.cert_id} className={i % 2 ? "bg-white" : "bg-slate-50/40"}>
                <Td tight>
                  <span className="font-mono text-slate-900 text-[9px] truncate block">
                    {c.cert_id}
                  </span>
                </Td>
                <Td tight>
                  <span className="font-semibold text-slate-900 truncate block">
                    {c.team_member_name}
                  </span>
                </Td>
                <Td tight>
                  <span className="truncate block">{c.module}</span>
                </Td>
                <Td tight align="center" className="font-mono text-slate-700">
                  {c.version || "1.0"}
                </Td>
                <Td
                  tight
                  align="center"
                  className="tabular-nums text-emerald-700 font-semibold"
                >
                  {c.score != null ? `${c.score}%` : "—"}
                </Td>
                <Td tight align="center" className="text-slate-700">
                  {formatDate(c.completed_at)}
                </Td>
                <Td tight align="center" className="text-slate-700">
                  {c.validUntil}
                </Td>
                <Td tight align="center">
                  <StatusBadge
                    tone={c.expired ? "rose" : c.expiredSoon ? "amber" : "emerald"}
                  >
                    {c.expired ? "Expired" : c.expiredSoon ? "Renew" : "Valid"}
                  </StatusBadge>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4 flex items-center gap-4">
        <div className="h-14 w-14 shrink-0 rounded-md border border-slate-200 bg-white p-1.5">
          <QrTile value={`${verifyOrigin || ""}/verify`} />
        </div>
        <div className="text-[10.5px] text-slate-600 leading-[1.55]">
          To verify any certificate, scan the QR on the individual certificate
          page or visit{" "}
          <span className="font-mono text-slate-900">
            {(verifyOrigin || "").replace(/^https?:\/\//, "")}/verify/&lt;CERT-ID&gt;
          </span>
          . Every record is SHA-256 signed and retained for 7 years.
        </div>
      </div>
    </PageFrame>
  );
}

/* ================================================================== *
 *  6 · Audit timeline
 * ================================================================== */
function TimelinePage({ business, rows, chunkIndex, totalChunks, pageIndex, pageMeta }) {
  return (
    <PageFrame
      testid={`dossier-timeline-${chunkIndex}`}
      header={
        <PageHeader
          kicker="Chronological events"
          title="Audit Timeline"
          subtitle={business?.name}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          label={`Audit timeline · page ${chunkIndex + 1} of ${totalChunks}`}
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <p className="text-[10.5px] text-slate-500 leading-[1.55] mb-4 max-w-3xl">
        Every published document, training assignment, assessment, certificate
        and manager approval — captured with a UTC timestamp.
      </p>

      <ol className="relative pl-5">
        <span className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" />
        {rows.map((e) => (
          <li key={e.id} className="relative pb-3 last:pb-0">
            <span
              className={`absolute -left-[13px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.06)] ${
                e.tone === "emerald"
                  ? "bg-emerald-500"
                  : e.tone === "gold"
                  ? "bg-[#D4A65A]"
                  : e.tone === "rose"
                  ? "bg-rose-500"
                  : "bg-slate-400"
              }`}
            />
            <div className="text-[8.5px] font-mono uppercase tracking-[0.14em] text-slate-500">
              {formatDateTime(e.at)}
            </div>
            <div className="text-[11px] text-slate-900 font-semibold leading-[1.4] mt-0.5">
              {e.label}
              <span className="font-normal text-slate-500"> · {e.actor}</span>
            </div>
            <div className="text-[10px] text-slate-500 truncate leading-[1.4]">
              {e.target}
              {e.detail ? ` · ${e.detail}` : ""}
              {e.reference ? ` · ${e.reference}` : ""}
            </div>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="text-[10.5px] text-slate-400">No events on this page.</li>
        )}
      </ol>
    </PageFrame>
  );
}

/* ================================================================== *
 *  7 · Exceptions & risks
 * ================================================================== */
function ExceptionsPage({ business, exceptions, pageIndex, pageMeta }) {
  const groups = [
    { key: "overdue", label: "Overdue training", tone: "amber", icon: Timer, rows: exceptions.overdue },
    {
      key: "failed",
      label: "Failed assessments",
      tone: "rose",
      icon: AlertTriangle,
      rows: exceptions.failed,
    },
    {
      key: "expired",
      label: "Expired certificates",
      tone: "rose",
      icon: CalendarClock,
      rows: exceptions.expired,
    },
    {
      key: "stale",
      label: "Documents awaiting review",
      tone: "amber",
      icon: FileClock,
      rows: exceptions.staleDocs,
    },
    {
      key: "unacknowledged",
      label: "Outstanding acknowledgements",
      tone: "amber",
      icon: ClipboardCheck,
      rows: exceptions.unacknowledged,
    },
  ];

  return (
    <PageFrame
      testid="dossier-exceptions"
      header={
        <PageHeader
          kicker="Where risk exists today"
          title="Exceptions & Risks"
          subtitle={business?.name}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          label="Exceptions & risks"
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <p className="text-[10.5px] text-slate-500 leading-[1.55] mb-4 max-w-3xl">
        A prioritised view of every compliance risk currently open in this
        workspace. Amber rows require follow-up; rose entries require immediate
        action.
      </p>

      {exceptions.total === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-4">
          <span className="h-10 w-10 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <ShieldCheck size={16} />
          </span>
          <div>
            <div className="font-display text-[16px] text-slate-900 font-semibold tracking-tight leading-[1.3]">
              No open exceptions.
            </div>
            <p className="mt-1 text-[11px] text-slate-600 leading-[1.55] max-w-xl">
              Every assignment is up to date, every certificate is within its
              validity window, every document is under active review and every
              acknowledgement is signed. This workspace is inspection ready.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.key} className="rounded-lg border border-slate-200 overflow-hidden">
              <div
                className={`flex items-center gap-2 px-3.5 py-2 border-b border-slate-200 ${
                  g.tone === "rose" ? "bg-rose-50" : "bg-amber-50"
                }`}
              >
                <g.icon
                  size={12}
                  className={g.tone === "rose" ? "text-rose-700" : "text-amber-700"}
                />
                <div
                  className={`text-[10px] uppercase tracking-[0.16em] font-semibold ${
                    g.tone === "rose" ? "text-rose-700" : "text-amber-800"
                  }`}
                >
                  {g.label}
                </div>
                <span className="ml-auto text-[9.5px] font-mono text-slate-600">
                  {g.rows.length} open
                </span>
              </div>
              <ul>
                {g.rows.slice(0, 4).map((r) => (
                  <li
                    key={r.id}
                    className="px-3.5 py-1.5 border-b border-slate-100 last:border-0 flex items-center gap-3 text-[10.5px]"
                  >
                    <span className="text-slate-900 font-semibold min-w-0 flex-1 truncate">
                      {r.title}
                    </span>
                    <span className="text-slate-500 text-[9.5px] shrink-0">
                      {r.detail}
                    </span>
                  </li>
                ))}
                {g.rows.length === 0 && (
                  <li className="px-3.5 py-2 text-[10.5px] text-slate-400">
                    None recorded.
                  </li>
                )}
                {g.rows.length > 4 && (
                  <li className="px-3.5 py-1.5 text-[9.5px] text-slate-500 bg-slate-50 font-mono">
                    +{g.rows.length - 4} more · see appendix
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </PageFrame>
  );
}

/* ================================================================== *
 *  8 · Evidence integrity
 * ================================================================== */
function IntegrityPage({ business, derived, verifyOrigin, pageIndex, pageMeta }) {
  const verifyUrl = `${verifyOrigin || ""}/verify?report=${encodeURIComponent(
    pageMeta.reportId
  )}`;
  const hash = derived.reportHash;
  return (
    <PageFrame
      testid="dossier-integrity"
      tone="dark"
      header={
        <PageHeader
          tone="dark"
          kicker="Digital signature & retention"
          title="Evidence Integrity"
          subtitle={business?.name}
          reportId={pageMeta.reportId}
        />
      }
      footer={
        <PageFooter
          tone="dark"
          label="Evidence integrity · end of dossier"
          pageIndex={pageIndex}
          totalPages={pageMeta.totalPages}
          generatedAt={pageMeta.generatedAt}
        />
      }
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.55) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-[#D4A65A]/15 blur-3xl" />
      <div className="pointer-events-none absolute -top-16 -left-16 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />

      <div className="relative grid grid-cols-[1fr_180px] gap-6 min-w-0">
        <div className="min-w-0">
          <div className="text-[9.5px] uppercase tracking-[0.22em] font-semibold text-[#F0D497]">
            Digital signature
          </div>
          <div className="mt-2 rounded-lg border border-white/15 bg-white/[0.04] px-4 py-3 font-mono text-[9px] text-slate-100 leading-[1.55] overflow-hidden break-all">
            {hash}
          </div>

          <div className="mt-5 text-[9.5px] uppercase tracking-[0.22em] font-semibold text-[#F0D497]">
            Retention policy
          </div>
          <ul className="mt-2 space-y-1 text-[10.5px] text-slate-200 leading-[1.55]">
            <li className="flex items-start gap-2">
              <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
              Signed certificates retained for 7 years minimum, SHA-256 chained.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
              Audit events retained for 7 years — append-only, tamper-evident.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
              Acknowledgements retained for 7 years, captured with signature,
              timestamp and device.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
              Data hosted in the EU (Ireland region), encrypted at rest.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
              Removed staff are tombstoned — historic evidence remains under
              their name.
            </li>
          </ul>

          <div className="mt-5 text-[9.5px] uppercase tracking-[0.22em] font-semibold text-[#F0D497]">
            Chain of custody
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2.5">
            <ChainStep
              label="Prepared"
              value="CanniComply"
              sub={formatDateTime(pageMeta.generatedAt)}
            />
            <ChainStep
              label="Signed"
              value={business?.name || "Owner"}
              sub="Digital signature above"
            />
            <ChainStep label="Retention" value="7 years min" sub="Immutable" />
          </div>

          <div className="mt-4 pt-4 border-t border-white/10 text-[10px] text-slate-300 leading-[1.55]">
            This dossier was{" "}
            <span className="text-[#F0D497] font-semibold">
              generated automatically by CanniComply
            </span>
            . Every record referenced herein remains verifiable from your
            workspace. Present alongside a supervised export from your record
            system when responding to an inspection.
          </div>
        </div>

        <aside className="space-y-3 min-w-0">
          <div className="rounded-lg border border-white/15 bg-white/[0.04] p-3 text-center">
            <div className="text-[8.5px] uppercase tracking-[0.22em] font-semibold text-[#F0D497]">
              Verify report
            </div>
            <div className="mt-2 mx-auto h-[150px] w-[150px] rounded-md bg-white p-2">
              <QrTile value={verifyUrl} />
            </div>
            <div className="mt-2 text-[8px] text-slate-300 font-mono leading-[1.4] overflow-hidden">
              <div className="truncate" title={verifyUrl}>
                {shortVerifyUrl(verifyUrl)}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-white/15 p-3">
            <div className="text-[8.5px] uppercase tracking-[0.22em] font-semibold text-[#F0D497]">
              Report metadata
            </div>
            <ul className="mt-2 space-y-1 text-[9.5px] text-slate-200">
              <MetaRow k="Report ID" v={<span className="font-mono">{pageMeta.reportId}</span>} />
              <MetaRow
                k="Generated"
                v={<span className="font-mono">{formatDateTime(pageMeta.generatedAt)}</span>}
              />
              <MetaRow
                k="Employees"
                v={<span className="tabular-nums">{derived.employees.length}</span>}
              />
              <MetaRow
                k="Certificates"
                v={<span className="tabular-nums">{derived.certificates.length}</span>}
              />
              <MetaRow
                k="Events"
                v={<span className="tabular-nums">{derived.timeline.length}</span>}
              />
            </ul>
          </div>
        </aside>
      </div>
    </PageFrame>
  );
}

function MetaRow({ k, v }) {
  return (
    <li className="flex items-center justify-between gap-2 border-b border-white/[0.05] pb-1 last:border-0 last:pb-0">
      <span className="text-slate-400">{k}</span>
      <span className="text-white truncate">{v}</span>
    </li>
  );
}

/* ================================================================== *
 *  Shared micro-components
 * ================================================================== */
// No whitespace-nowrap here: the wrapper is overflow-hidden and a header has no
// truncation fallback, so any label wider than its column ("Completed" at 17%)
// was clipped mid-word. Wrapping costs a line; clipping costs the reader the
// column heading.
function Th({ children, align = "left", tight }) {
  return (
    <th
      className={`${
        tight ? "py-1.5" : "py-2"
      } px-2.5 border-b border-slate-200 font-semibold`}
      style={{ textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = "left", tight, className = "" }) {
  return (
    <td
      className={`${
        tight ? "py-1.5" : "py-2"
      } px-2.5 border-b border-slate-100 align-middle overflow-hidden ${className}`}
      style={{ textAlign: align }}
    >
      {children}
    </td>
  );
}
function Kv({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[8.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-[11px] text-slate-900 font-semibold truncate">{value}</div>
    </div>
  );
}
function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="mt-4 flex items-center gap-1.5">
      {Icon && <Icon size={11} className="text-[#8C6B33]" />}
      <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-slate-700">
        {children}
      </div>
    </div>
  );
}
function StatusBadge({ tone = "slate", children }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    gold: "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-[1px] text-[8.5px] uppercase tracking-[0.1em] font-semibold whitespace-nowrap ${
        toneMap[tone] || toneMap.slate
      }`}
    >
      {children}
    </span>
  );
}
function RiskDot({ risk }) {
  const map = {
    low: { color: "#059669", label: "G" },
    medium: { color: "#D97706", label: "A" },
    high: { color: "#E11D48", label: "R" },
  };
  const m = map[risk] || map.medium;
  return (
    <span className="inline-flex items-center gap-1 justify-center">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ background: m.color }}
      />
      <span
        className="text-[9px] font-mono font-semibold"
        style={{ color: m.color }}
      >
        {m.label}
      </span>
    </span>
  );
}
function KpiTile({ label, value, sub, tone = "slate" }) {
  const toneMap = {
    slate: "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    gold: "bg-[#FDF6E7] border-[#D4A65A]/40 text-[#8C6B33]",
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[8.5px] uppercase tracking-[0.18em] font-semibold opacity-80 truncate">
        {label}
      </div>
      <div className="mt-1 font-display text-[22px] leading-[1.15] font-semibold tabular-nums tracking-tight">
        {value}
      </div>
      {sub && <div className="mt-1 text-[9.5px] opacity-70 truncate">{sub}</div>}
    </div>
  );
}
function ChainStep({ label, value, sub }) {
  return (
    <div className="rounded-md border border-white/15 bg-white/[0.04] p-2.5">
      <div className="text-[8.5px] uppercase tracking-[0.18em] font-semibold text-[#F0D497]">
        {label}
      </div>
      <div className="mt-1 text-[11px] font-semibold text-white truncate">{value}</div>
      <div className="text-[8.5px] text-slate-400 font-mono truncate">{sub}</div>
    </div>
  );
}
function ScoreRing({ score }) {
  const size = 168;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#dossierRing)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${dash} ${c}`}
        />
        <defs>
          <linearGradient id="dossierRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4A65A" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-[42px] leading-[1.05] tracking-tight text-white font-semibold tabular-nums">
          {score}%
        </div>
        <div className="text-[8.5px] uppercase tracking-[0.22em] text-slate-300 mt-1.5 font-semibold">
          Compliant
        </div>
      </div>
    </div>
  );
}
function QrTile({ value }) {
  const dataUrl = useMemo(() => (value ? buildQrDataUrl(value) : null), [value]);
  if (!dataUrl) {
    return <div className="h-full w-full rounded bg-slate-100" />;
  }
  return <img src={dataUrl} alt="Verification QR" className="h-full w-full object-contain" />;
}

/* ================================================================== *
 *  Derived metrics — unchanged data shape from the previous release
 * ================================================================== */
function deriveMetrics({
  summary,
  team,
  docs,
  certs,
  assignments,
  competencyAssignments,
  compliance_settings,
}) {
  const now = Date.now();
  const validityMonths = compliance_settings.certificate_validity_months || 12;
  const warnBeforeDays = compliance_settings.certificate_warn_before_days || 30;
  const overdueAfterDays = compliance_settings.overdue_after_days || 14;
  const staleMonths = 12;

  const enrichedCerts = certs
    .map((c) => {
      const issued = c.completed_at ? new Date(c.completed_at).getTime() : null;
      const validUntilTs = issued ? issued + validityMonths * 30 * 86400000 : null;
      const expired = validUntilTs && validUntilTs < now;
      const expiredSoon =
        validUntilTs && !expired && (validUntilTs - now) / 86400000 < warnBeforeDays;
      return {
        ...c,
        _validUntilTs: validUntilTs,
        validUntil: validUntilTs
          ? formatDate(new Date(validUntilTs).toISOString())
          : "—",
        expired: !!expired,
        expiredSoon: !!expiredSoon,
      };
    })
    .sort(
      (a, b) =>
        (new Date(b.completed_at).getTime() || 0) -
        (new Date(a.completed_at).getTime() || 0)
    );

  const enrichedDocs = docs.map((d) => {
    const docAssignments = assignments.filter((a) => a.document_id === d.id);
    const passedCount = docAssignments.filter((a) => a.status === "completed").length;
    const certCount = certs.filter(
      (c) => c.module && d.title && c.module.includes(d.title.split(" ")[0])
    ).length;
    const createdMs = d.created_at ? new Date(d.created_at).getTime() : 0;
    const ageMonths = createdMs ? (now - createdMs) / (86400000 * 30) : 0;
    const stale = ageMonths > staleMonths;
    const nextReview = createdMs
      ? formatDate(new Date(createdMs + staleMonths * 30 * 86400000).toISOString())
      : "—";
    return {
      ...d,
      assignedCount: d.assigned_count ?? docAssignments.length,
      completedCount: d.completed_count ?? passedCount,
      passedCount,
      certCount,
      nextReview: stale ? `${nextReview} · due` : nextReview,
      reviewStatus: stale ? "Review due" : "Current",
      reviewTone: stale ? "amber" : "emerald",
      _stale: stale,
    };
  });

  const employees = team.map((m) => {
    const memberComps = competencyAssignments.filter((c) => c.team_member_id === m.id);
    const completedComps = memberComps.filter((c) => c.status === "completed");
    const memberDocs = assignments.filter((a) => a.team_member_id === m.id);
    const memberCerts = certs.filter((c) => c.team_member_id === m.id);
    const memberOutstanding = memberDocs.filter((a) => a.status !== "completed");
    const trainingPct =
      m.assignments_total > 0
        ? Math.round(((m.assignments_completed || 0) / m.assignments_total) * 100)
        : memberComps.length > 0
        ? Math.round((completedComps.length / memberComps.length) * 100)
        : 100;
    const lastScore =
      memberCerts.length > 0 ? memberCerts[0].score : completedComps[0]?.score ?? null;
    const overdueItems = memberOutstanding.filter(
      (a) =>
        a.assigned_at &&
        (now - new Date(a.assigned_at).getTime()) / 86400000 > overdueAfterDays
    );
    const risk =
      overdueItems.length > 0 ? "high" : memberOutstanding.length > 0 ? "medium" : "low";
    const statusLabel =
      risk === "low" ? "Compliant" : risk === "medium" ? "In progress" : "Action required";
    return {
      id: m.id,
      name: m.name,
      email: m.email,
      role: m.role || m.job_title,
      department: m.department,
      competenciesAssigned: memberComps.length,
      competenciesCompleted: completedComps.length,
      trainingPct,
      certificates: memberCerts.length,
      lastScore,
      openActions: memberOutstanding.length,
      risk,
      statusLabel,
      competencyRows: memberComps,
      documentRows: memberDocs,
      certificateRows: memberCerts,
      outstanding: memberOutstanding.map((o) => ({
        id: o.id,
        label: `${o.document_title || "Document"} ${o.document_version || ""} — assigned ${formatDate(
          o.assigned_at
        )}`,
      })),
      approvedBy: null,
    };
  });

  const timelineRaw = [
    ...docs.map((d) => ({
      id: `doc-${d.id}`,
      at: d.created_at,
      label: "Document published",
      actor: "Owner",
      target: `${d.title} ${d.version || ""}`,
      detail: d.description,
      reference: d.id?.slice(0, 8),
      tone: "slate",
    })),
    ...assignments.map((a) => ({
      id: `assign-${a.id}`,
      at: a.assigned_at,
      label: "Training assigned",
      actor: "Owner",
      target: `${a.team_member_name || "Team member"} ← ${a.document_title || "Document"}`,
      detail: a.document_version,
      reference: a.id?.slice(0, 8),
      tone: "gold",
    })),
    ...assignments
      .filter((a) => a.status === "completed" && a.completed_at)
      .map((a) => ({
        id: `assess-${a.id}`,
        at: a.completed_at,
        label: "Assessment completed",
        actor: a.team_member_name || "Team member",
        target: a.document_title || "Document",
        detail: a.cert_id ? `cert ${a.cert_id}` : null,
        reference: a.id?.slice(0, 8),
        tone: "emerald",
      })),
    ...certs.map((c) => ({
      id: `cert-${c.cert_id}`,
      at: c.completed_at,
      label: "Certificate issued",
      actor: c.team_member_name,
      target: c.module,
      detail: `score ${c.score}%`,
      reference: c.cert_id,
      tone: "emerald",
    })),
  ]
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const overdue = assignments
    .filter(
      (a) =>
        a.status !== "completed" &&
        a.assigned_at &&
        (now - new Date(a.assigned_at).getTime()) / 86400000 > overdueAfterDays
    )
    .map((a) => ({
      id: a.id,
      title: `${a.team_member_name || "Team member"} — ${a.document_title || "Document"}`,
      detail: `overdue ${Math.round(
        (now - new Date(a.assigned_at).getTime()) / 86400000
      )}d`,
    }));
  const failed = competencyAssignments
    .filter(
      (c) =>
        c.last_attempt_score_pct != null &&
        c.status !== "completed" &&
        c.pass_mark &&
        c.last_attempt_score_pct < c.pass_mark
    )
    .map((c) => ({
      id: c.id,
      title: `${c.team_member_name} — ${c.competency_name}`,
      detail: `${c.last_attempt_score_pct}% (pass ${c.pass_mark})`,
    }));
  const expired = enrichedCerts
    .filter((c) => c.expired)
    .map((c) => ({
      id: c.cert_id,
      title: `${c.team_member_name} — ${c.module}`,
      detail: `expired ${c.validUntil}`,
    }));
  const staleDocsList = enrichedDocs
    .filter((d) => d._stale)
    .map((d) => ({
      id: d.id,
      title: `${d.title} ${d.version || ""}`,
      detail: "review overdue",
    }));
  const unacknowledged = assignments
    .filter(
      (a) =>
        a.status !== "completed" &&
        a.assigned_at &&
        (now - new Date(a.assigned_at).getTime()) / 86400000 <= overdueAfterDays
    )
    .map((a) => ({
      id: a.id,
      title: `${a.team_member_name || "Team member"} — ${a.document_title || "Document"}`,
      detail: `assigned ${formatDate(a.assigned_at)}`,
    }));
  const exceptionsTotal =
    overdue.length +
    failed.length +
    expired.length +
    staleDocsList.length +
    unacknowledged.length;

  const assessmentsPassed = enrichedCerts.length;
  const avgScore =
    enrichedCerts.length > 0
      ? Math.round(
          enrichedCerts.reduce((s, c) => s + (c.score || 0), 0) / enrichedCerts.length
        )
      : 0;
  const expiringCount = enrichedCerts.filter((c) => c.expiredSoon).length;

  const hashSource = JSON.stringify({
    b: summary?.business_name,
    s: summary?.compliance_score,
    n: team.length,
    d: docs.length,
    c: certs.length,
    at: Date.now(),
  });
  const reportHash = buildPseudoHash(hashSource);

  return {
    employees,
    documents: enrichedDocs,
    certificates: enrichedCerts,
    timeline: timelineRaw,
    exceptions: {
      overdue,
      failed,
      expired,
      staleDocs: staleDocsList,
      unacknowledged,
      total: exceptionsTotal,
    },
    assessmentsPassed,
    avgScore,
    expiringCount,
    reportHash,
  };
}

/* ================================================================== *
 *  Helpers
 * ================================================================== */
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out.length === 0 ? [[]] : out;
}
function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = iso instanceof Date ? iso : new Date(iso);
    if (!Number.isFinite(d.getTime())) return "—";
    return d
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", " ·");
  } catch {
    return "—";
  }
}
function readinessLabel(score, exceptions) {
  if (exceptions > 0 && score < 80) {
    return {
      label: "Action required",
      tone: "text-rose-300",
      body:
        "Open exceptions and low completion mean this workspace would not clear an inspection today. Address the risks listed on the Exceptions page.",
    };
  }
  if (score >= 95) {
    return {
      label: "Inspection ready",
      tone: "text-emerald-300",
      body:
        "Assignments are up to date, evidence is signed and every document is under active review. This workspace can present to an inspector today.",
    };
  }
  if (score >= 80) {
    return {
      label: "On track",
      tone: "text-emerald-200",
      body:
        "The vast majority of assignments are complete. A small number of open items remain — see the Exceptions page for detail.",
    };
  }
  if (score >= 60) {
    return {
      label: "Making progress",
      tone: "text-[#F0D497]",
      body:
        "Training is under way but not yet at the level a regulator expects. Prioritise the exceptions on the following pages before scheduling an inspection.",
    };
  }
  return {
    label: "Getting started",
    tone: "text-[#F0D497]",
    body:
      "The workforce is early in its training cycle. Continue assigning documents and competencies before treating this dossier as inspection evidence.",
  };
}
function narrowToEmployee(full, emp) {
  const nameNeedle = (emp.name || "").toLowerCase();
  const idNeedle = emp.id;
  const isForEmployee = (row) => {
    if (!row) return false;
    if (row.team_member_id && row.team_member_id === idNeedle) return true;
    if (row.actor && String(row.actor).toLowerCase() === nameNeedle) return true;
    if (row.target && String(row.target).toLowerCase().includes(nameNeedle)) return true;
    if (row.title && String(row.title).toLowerCase().includes(nameNeedle)) return true;
    return false;
  };
  return {
    ...full,
    employees: [emp],
    certificates: full.certificates.filter((c) => c.team_member_id === idNeedle),
    timeline: full.timeline.filter(isForEmployee),
    documents: full.documents, // documents themselves aren't rendered in focus mode
    exceptions: {
      overdue: full.exceptions.overdue.filter(isForEmployee),
      failed: full.exceptions.failed.filter(isForEmployee),
      expired: full.exceptions.expired.filter(isForEmployee),
      staleDocs: [], // workspace-wide, not employee-specific
      unacknowledged: full.exceptions.unacknowledged.filter(isForEmployee),
      total: 0,
    },
  };
}

function readinessForEmployee(emp, exceptionTotal) {
  if (emp.risk === "high") {
    return {
      label: "Action required",
      tone: "text-rose-300",
      body:
        "This employee has overdue training or unresolved exceptions. Address the risks listed on the Exceptions page before treating this record as inspection evidence.",
    };
  }
  if (emp.risk === "medium") {
    return {
      label: "In progress",
      tone: "text-[#F0D497]",
      body:
        "This employee has open assignments still within their working window. Continue monitoring and encourage completion before the overdue threshold.",
    };
  }
  return {
    label: "Compliant",
    tone: "text-emerald-300",
    body:
      "This employee's assigned training is complete, their certificates are signed and no exceptions are open against their record.",
  };
}

function buildEmployeeVerifyUrl(origin, emp) {
  const base = (origin || "").replace(/\/+$/, "");
  return `${base}/verify?employee=${encodeURIComponent(emp.id)}`;
}

/**
 * Render a fixed-width sidebar URL that never overflows its container
 * while still uniquely identifying the record. Full URL stays in the
 * QR code + a `title` tooltip; this string is the human-readable label
 * that has to fit inside the 180 px right rail without break-all
 * shattering it into three lines.
 */
function shortVerifyUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.host.replace(/^www\./, "");
    const emp = (u.searchParams.get("employee") || "").slice(0, 8);
    return emp
      ? `${host}/verify?employee=${emp}\u2026`
      : `${host}${u.pathname || ""}`;
  } catch {
    // Fallback for non-URL inputs — keep first + last chars visible.
    return url.length > 40 ? `${url.slice(0, 24)}\u2026${url.slice(-10)}` : url;
  }
}
function buildQrDataUrl(value) {
  try {
    const qr = QRCode.create(value, { errorCorrectionLevel: "M" });
    const modules = qr.modules;
    const size = modules.size;
    const cell = 6;
    const total = size * cell;
    const rects = [];
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        if (modules.get(x, y)) {
          rects.push(
            `<rect x="${x * cell}" y="${y * cell}" width="${cell}" height="${cell}" fill="#0B1220"/>`
          );
        }
      }
    }
    // width/height are required, not decorative: a viewBox-only SVG has no
    // intrinsic size, and html2canvas then rasterises it against a default
    // size for the PDF export - which renders a blown-up crop of the QR's
    // top-left finder pattern instead of the code. On-screen CSS sizing hides
    // the problem, so it only ever surfaced in the downloaded dossier.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${total}" height="${total}" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#ffffff"/>${rects.join(
      ""
    )}</svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  } catch {
    return null;
  }
}
function buildPseudoHash(input) {
  const alphabet = "0123456789abcdef";
  let seed = 0;
  for (let i = 0; i < input.length; i += 1) {
    seed = (seed * 31 + input.charCodeAt(i)) & 0xffffffff;
  }
  let s = seed >>> 0;
  const out = [];
  for (let i = 0; i < 64; i += 1) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    out.push(alphabet[s & 0xf]);
  }
  return out.join("");
}
